import {
  TeModal,
  TeContainer,
  TeContainerSlim,
  TeText,
  TeButton,
  TeLabel,
  TeButtonMinimal,
  TeForm,
  TeIcon,
} from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import {
  resetFormWithName,
  submitFormWithName,
} from '@topcon/tuicl/dist/components/form/form.utils';

import ModalItems from '../../../components/Modal/Modaltems';
import { useAcceptRequestToJoin } from './requestsToJoin.mutations';
import ProfileSelector from '../../../components/ProfileSelector';
import { RequestToJoinBody } from '../../../api/generated/pap';
import { useProcessJoinRequests } from './useProcessJoinRequests';

interface Props {
  requests: RequestToJoinBody[];
  open: boolean;
  onClose: (shouldClose: boolean) => void;
}

function AcceptRequestModal({ requests, open, onClose }: Props) {
  const { t } = useTranslation('users');
  const { mutateAsync: acceptRequestToJoin } = useAcceptRequestToJoin();
  const [requestProfiles, setRequestProfiles] = useState<string[]>([]);
  const [showMore, setShowMore] = useState<boolean>(false);
  const { headers, data, processRequests } = useProcessJoinRequests(requests, 'accepted', onClose);

  const resetAcceptRequestsModal = (shouldUpdate: boolean) => {
    onClose(shouldUpdate);
    setShowMore(false);
    setTimeout(() => {
      resetFormWithName('acceptRequestsForm');
    }, 200); // TODO: Move timeout to constant
  };

  const handleAcceptRequest = () =>
    processRequests((req) =>
      acceptRequestToJoin({
        identityId: req.identityId,
        profileIds: requestProfiles,
      })
    );

  // TODO: Maybe this could use ConfirmDeletionModal as well? I don't have data to test anything :D
  return (
    <TeModal
      variant="dark"
      open={open}
      closeMode="button"
      teclose={() => resetAcceptRequestsModal(false)}
      modalTitle={t('requestModal.assignProfiles')}
      contentPadding={0}
      closeable
    >
      <TeContainer backgroundColor="background.primary" width={640} padding={['m', 's']} fill>
        <TeForm name="acceptRequestsForm" tesubmit={handleAcceptRequest}>
          <TeContainerSlim gap="l" column grow>
            {data && data.length > 1 ? (
              <TeContainer
                gap="m"
                backgroundColor="background.secondary"
                justifyContent="center"
                borderRadius={4}
                column
                fill
              >
                <TeContainerSlim justifyContent="space-between" alignItems="center" fill>
                  <TeContainerSlim gap="xs2">
                    <TeIcon name="info" />
                    <TeText>{t('requestModal.profileInfoText', { count: data.length })}</TeText>
                  </TeContainerSlim>
                  <TeButtonMinimal
                    text={showMore ? t('requestModal.hide') : t('requestModal.show')}
                    size="xs"
                    teclick={() => setShowMore((prev) => !prev)}
                  />
                </TeContainerSlim>
                {showMore && (
                  <ModalItems
                    headers={headers}
                    data={data}
                    iconName="account_circle"
                    iconColor="icon.muted"
                  />
                )}
              </TeContainer>
            ) : (
              <TeContainerSlim gap="s" column fill>
                <TeContainerSlim gap="xs2">
                  <TeIcon name="info" />
                  <TeText>{t('requestModal.profileInfoText', { count: data.length })}</TeText>
                </TeContainerSlim>
                <ModalItems
                  headers={headers}
                  data={data}
                  iconName="account_circle"
                  iconColor="icon.muted"
                />
              </TeContainerSlim>
            )}

            <TeContainerSlim gap="s" column fill>
              <TeContainerSlim justifyContent="space-between" alignItems="center" fill>
                <TeLabel text={t('requestModal.profileLabel')} />
                <TeContainerSlim gap="xs2" alignItems="center">
                  <TeIcon name="info" color="icon.secondary" />
                  <TeText color="text.secondary" size={12}>
                    {t('requestModal.profileHelp')}
                  </TeText>
                </TeContainerSlim>
              </TeContainerSlim>
              <ProfileSelector profiles={requestProfiles} setProfiles={setRequestProfiles} />
            </TeContainerSlim>
          </TeContainerSlim>
        </TeForm>
      </TeContainer>

      <TeButton
        slot="primary"
        text={t('requestModal.acceptRequests', { count: data.length })}
        variant="primary"
        size="s"
        teclick={() => submitFormWithName('acceptRequestsForm')}
        disabled={requests.length === 0 || requestProfiles.length === 0}
      />
      <TeButton
        slot="secondary"
        text={t('cancel')}
        variant="neutral"
        size="s"
        teclick={() => resetAcceptRequestsModal(false)}
      />
    </TeModal>
  );
}

export default AcceptRequestModal;
